import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryItems(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = ["All", ...new Set(galleryItems.map(item => item.category))];

  const filteredItems = filter === "All" 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-copper animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="gallery-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-copper text-sm tracking-[0.3em] uppercase mb-4 font-body">Portfolio</p>
          <h1 
            data-testid="gallery-title"
            className="font-heading text-4xl md:text-6xl mb-6"
          >
            <span className="copper-text">Gallery</span>
          </h1>
          <p className="font-body text-gray-400 max-w-2xl mx-auto">
            Each piece is a unique creation, handcrafted with copper wire to bring your images to life in a way never seen before.
          </p>
        </div>

        {/* Filter Buttons */}
        <div 
          data-testid="gallery-filters"
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              data-testid={`filter-${category.toLowerCase()}`}
              onClick={() => setFilter(category)}
              className={`font-body text-sm tracking-widest uppercase px-6 py-2 transition-all duration-300 border ${
                filter === category
                  ? "border-copper text-copper"
                  : "border-white/10 text-gray-400 hover:border-copper/50 hover:text-copper"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid - Masonry Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              data-testid={`gallery-item-${index}`}
              className={`relative overflow-hidden group cursor-pointer border border-white/5 hover:border-copper/30 transition-all duration-500 ${
                index % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              onClick={() => setSelectedImage(item)}
            >
              <div className={`aspect-square ${index % 5 === 0 ? "md:aspect-auto md:h-full md:min-h-[500px]" : ""}`}>
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = `https://images.unsplash.com/photo-1601069151015-0a7fb104503c?w=600&h=600&fit=crop`;
                  }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <div>
                  <p className="text-copper text-xs tracking-widest uppercase mb-2">{item.category}</p>
                  <h3 className="font-heading text-xl text-white mb-1">{item.title}</h3>
                  <p className="font-body text-gray-400 text-sm">{item.size}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-24">
            <p className="text-gray-400 font-body">No items found in this category.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          data-testid="lightbox-modal"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-12"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="close-lightbox-btn"
            className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <div className="max-w-5xl w-full flex flex-col md:flex-row gap-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex-1">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain"
                onError={(e) => {
                  e.target.src = `https://images.unsplash.com/photo-1601069151015-0a7fb104503c?w=800&fit=crop`;
                }}
              />
            </div>
            <div className="md:w-80 flex flex-col justify-center">
              <p className="text-copper text-xs tracking-widest uppercase mb-2">{selectedImage.category}</p>
              <h2 className="font-heading text-3xl text-white mb-4">{selectedImage.title}</h2>
              <p className="font-body text-gray-400 mb-4">{selectedImage.description}</p>
              <p className="font-body text-gray-500 text-sm">Size: {selectedImage.size}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
